import { z } from "zod";
import type { VenueBranding } from "../theme";

/**
 * Public, ANONYMOUS venue booking config — the small read the shell needs BEFORE any booking UI to
 * decide which shell state to render and how to theme, PLUS the diner-journey parameters
 * (tasks/diner-booking-ui.md): the resolved `venueId` the venueId-keyed public endpoints take, the
 * venue `timeZone` for venue-local rendering (localization-i18n R5), and the per-service `booking`
 * block (mode, party-max routing threshold SC-5, zones, terms text + policy version, contact). Stubbed
 * by MSW here; tasks/diner-booking-api.md lands the real slug→config discovery endpoint. The `status`
 * maps to the availability rejections the shell renders as fail-safe frames (DOMAIN.md §C.6
 * `OnlineBookingDisabled` / `VenueNotLive`).
 */
export const venueBookingStatus = z.enum(["live", "online_booking_disabled", "not_live"]);
export type VenueBookingStatus = z.infer<typeof venueBookingStatus>;

export const brandingSchema: z.ZodType<VenueBranding> = z.object({
  primaryColor: z.string().optional(),
  onPrimaryColor: z.string().optional(),
  accentColor: z.string().optional(),
  surfaceColor: z.string().optional(),
  radius: z.string().optional(),
  fontFamily: z.string().optional(),
});

/** The service booking mode the API returns (DOMAIN.md §C.4): drives instant-confirm vs request UI. */
export const bookingModeSchema = z.enum(["INSTANT_CONFIRM", "REQUEST_TO_BOOK"]);
export type BookingMode = z.infer<typeof bookingModeSchema>;

/** A selectable seating zone (DOMAIN.md §C.5). The zone radio group renders ONLY when this is non-empty. */
export const zoneOptionSchema = z.object({ zoneId: z.string(), name: z.string() });
export type ZoneOption = z.infer<typeof zoneOptionSchema>;

/** Venue contact for the CONTACT_FALLBACK screen (online-booking-off / not-live edge cases). */
export const venueContactSchema = z.object({
  // The API sends `null` (not absent) when a live venue has no public phone/email. Accept null AND
  // absent, but normalize null → undefined so the output stays `string | undefined` (else a
  // contactless-but-bookable venue fails validation and renders the error frame). Preprocessing
  // keeps every downstream consumer's `string | undefined` type unchanged.
  phone: z.preprocess((v) => v ?? undefined, z.string().optional()),
  email: z.preprocess((v) => v ?? undefined, z.string().optional()),
});
export type VenueContact = z.infer<typeof venueContactSchema>;

/**
 * The booking-journey parameters carried by a LIVE venue's config. Every field defaults, so a config
 * that omits the block still yields a usable (instant-confirm, no-zones) journey — resilience over the
 * not-yet-landed discovery endpoint. `partySizeMax` is the large-party routing threshold (SC-5): a
 * party above it never issues a slot query, it flips straight to the request path.
 */
export const bookingConfigSchema = z.object({
  bookingMode: bookingModeSchema.default("INSTANT_CONFIRM"),
  partySizeMax: z.number().int().positive().default(8),
  /** Booking horizon in days — bounds the date picker's `max` so beyond-horizon dates are never offered. */
  horizonDays: z.number().int().positive().default(60),
  zones: z.array(zoneOptionSchema).default([]),
  /** Cancellation / no-show terms text shown above the acknowledgement checkbox (R3). */
  termsText: z.string().default(""),
  /** The policy version the DINER_TERMS_ACK rides with on the create request (DOMAIN.md §C.12). */
  policyVersion: z.string().default("v1"),
  /** The primary service the large-party request path submits against (SC-5 has no live slot). */
  defaultServiceId: z.string().default(""),
  contact: venueContactSchema.default({}),
  /** True when the venue belongs to a group — enables the cross-venue overflow proposal (R6). */
  isGroup: z.boolean().default(false),
  /**
   * Whether the service requires a card guarantee at booking (card-fingerprint-no-show.md §C.4
   * `Service.requireCreditCard`): NONE, REQUIRED (always), or CONDITIONAL (party at/above the
   * threshold). The requirement + snapshot fee/cutoff are ultimately evaluated + returned by the
   * create write path; these fields express the service policy that drives it.
   */
  requireCreditCard: z.enum(["NONE", "REQUIRED", "CONDITIONAL"]).optional(),
  /** CONDITIONAL only: party size at/above which the guarantee applies. */
  guaranteePartyThreshold: z.number().int().positive().optional(),
  /** No-show fee in integer minor units, snapshotted onto the booking when a guarantee is required. */
  noShowFeeMinor: z.number().int().nonnegative().optional(),
  /** Free-cancellation window before the slot (seconds); the cutoff instant is `startSec − this`. */
  noShowCutoffSeconds: z.number().int().nonnegative().optional(),
});
export type BookingConfig = z.infer<typeof bookingConfigSchema>;

export const venueConfigSchema = z.object({
  /** The resolved venue UUID the anonymous `/api/public/v1/venues/{venueId}/…` endpoints take. */
  venueId: z.string(),
  venueSlug: z.string(),
  venueName: z.string(),
  status: venueBookingStatus,
  /** BCP-47-ish default the diner language resolver falls back to (localization-i18n R1). */
  defaultLanguage: z.string().default("fr"),
  /** IANA venue timezone (DOMAIN.md §B.4/§C.1) — all diner-facing dates render venue-local, not device. */
  timeZone: z.string().default("Europe/Paris"),
  /**
   * Venue logo (LIVE `Logo` asset, DOMAIN.md §C.11) for the standalone page's reassurance header — an
   * Instagram/bio-link visitor should see they landed at the right venue (tasks/booking-widget-embed.md
   * standalone-link decision). Optional: absent → a clean name-only header. Served from the media CDN
   * over HTTPS (allowed by the /v/* CSP `img-src https:`).
   */
  logoUrl: z.string().url().optional(),
  // The API sends `null` (not absent) when a venue has no site branding. Accept null AND absent, but
  // normalize null → undefined so downstream stays `VenueBranding | undefined` (else an unbranded
  // venue fails validation and renders the error frame — mirrors `venueContactSchema` above).
  branding: z.preprocess((v) => v ?? undefined, brandingSchema.optional()),
  /**
   * Present for a live venue; the API sends an explicit `null` (not an absent key) for a
   * disabled/not-live venue. Normalize null → undefined so a not-live venue parses to the "unavailable"
   * frame instead of throwing into the error frame (same null-tolerance as `branding`/`contact`).
   */
  booking: z.preprocess((v) => v ?? undefined, bookingConfigSchema.optional()),
});

export type VenueConfig = z.infer<typeof venueConfigSchema>;

/** The journey's booking parameters with every default filled — safe even when `booking` is absent. */
export function resolveBookingConfig(config: VenueConfig): BookingConfig {
  return config.booking ?? bookingConfigSchema.parse({});
}
